import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, YoutubeLogo, PlayCircle } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = { title: "", description: "", youtube_url: "", category: "", model: "", system: "", tags: "" };

export default function AdminVideos() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => { setLoading(true); adminApi.get("/admin/videos").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...BLANK }); setOpen(true); };
  const openEdit = (r) => { setEditing({ ...r, tags: (r.tags || []).join(", ") }); setOpen(true); };

  const save = async () => {
    if (!editing.title || !editing.youtube_url) { toast.error("Title and YouTube URL are required"); return; }
    const payload = { ...editing, tags: (editing.tags || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing.id) await adminApi.put(`/admin/videos/${editing.id}`, payload);
      else await adminApi.post("/admin/videos", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await adminApi.delete(`/admin/videos/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <AdminHeader title="Videos" subtitle="YouTube tutorials Nick can recommend and customers can browse" actions={<PrimaryButton onClick={openNew} data-testid="video-new"><Plus size={16} /> Add Video</PrimaryButton>} />
      <div className="p-8">
        {loading ? <div className="text-neutral-600">Loading...</div> : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3">
            <YoutubeLogo size={36} className="text-neutral-700" weight="duotone" /> No videos yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((r) => (
              <div key={r.id} data-testid="admin-video-card" className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-hidden">
                <div className="relative aspect-video bg-black">
                  {r.thumbnail ? <img src={r.thumbnail} alt={r.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-700"><YoutubeLogo size={32} /></div>}
                  <a href={r.youtube_url} target="_blank" rel="noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors">
                    <PlayCircle size={40} weight="fill" className="text-white/90" />
                  </a>
                </div>
                <div className="p-4">
                  <div className="font-heading font-semibold text-white leading-tight mb-1 truncate">{r.title}</div>
                  <div className="flex flex-wrap gap-1.5 my-2">
                    {r.category && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#73bf44]/10 text-[#73bf44] border border-[#73bf44]/30">{r.category}</span>}
                    {r.model && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-black/40 border border-neutral-700 text-neutral-400">{r.model}</span>}
                  </div>
                  <div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(r)} data-testid="video-edit" className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><PencilSimple size={16} /></button>
                    <button onClick={() => del(r)} data-testid="video-delete" className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">{editing?.id ? "Edit" : "Add"} Video</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Field label="Title"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} data-testid="video-title" placeholder="Resetting the PM335" /></Field>
              <Field label="YouTube URL"><input value={editing.youtube_url} onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })} className={inputCls} data-testid="video-url" placeholder="https://www.youtube.com/watch?v=..." /></Field>
              <Field label="Description"><textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} className={inputCls} /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Category"><input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls} placeholder="Solar" /></Field>
                <Field label="Model"><input value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} className={inputCls} placeholder="Adventure" /></Field>
                <Field label="System"><input value={editing.system} onChange={(e) => setEditing({ ...editing, system: e.target.value })} className={inputCls} placeholder="PM335" /></Field>
              </div>
              <Field label="Tags (comma separated)"><input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={inputCls} placeholder="pm335, reset" /></Field>
              <div className="flex justify-end gap-2 pt-2">
                <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={save} data-testid="video-save">Save</PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
